import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';

export interface SocialLink {
  name: string;
  url: string;
  iconSvg?: ReturnType<typeof html>;
}

@customElement('rftw-footer')
export class RftwFooter extends LitElement {
  /** Brand Title */
  @property({ type: String }) brandTitle = 'Ride for the Wild';

  /** Tagline / Brief Description */
  @property({ type: String }) description =
    'Rodadas off-road que financian el rescate de vida silvestre en Costa Rica.';

  /** Label above social media links */
  @property({ type: String }) socialLabel = 'Síguenos';

  /** Copyright notice text */
  @property({ type: String }) copyrightText =
    '© 2026 Ride for the Wild · Costa Rica';

  /** Social media accounts list */
  @property({ type: Array }) socialLinks: SocialLink[] = [
    {
      name: 'Instagram',
      url: 'https://instagram.com/rftw',
    },
    {
      name: 'Facebook',
      url: '#',
    },
    {
      name: 'YouTube',
      url: '#',
    },
    {
      name: 'Strava / GPX',
      url: '#',
    },
  ];

  static styles = css`
    :host {
      display: block;
      width: 100%;
      box-sizing: border-box;
    }

    footer {
      background-color: #181715;
      color: #9e9d98;
      padding: 3.5rem 2rem 2rem;
      box-sizing: border-box;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .footer-wrapper {
      max-width: 1200px;
      margin: 0 auto;
    }

    /* Top row layout */
    .top-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 2rem;
      margin-bottom: 2.5rem;
    }

    .brand-section {
      max-width: 450px;
    }

    .brand-title {
      font-size: 1.35rem;
      font-weight: 700;
      color: #ffffff;
      margin: 0 0 0.5rem 0;
      letter-spacing: -0.01em;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .brand-description {
      font-size: 0.9rem;
      line-height: 1.5;
      color: #9e9d98;
      margin: 0 0 0.75rem;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .contact-email {
      color: #ffffff;
      font-size: 0.9rem;
      text-decoration-color: #8b8244;
      text-underline-offset: 0.25rem;
      overflow-wrap: anywhere;
    }

    .contact-email:hover,
    .contact-email:focus-visible {
      color: #d8c96e;
      outline: none;
    }

    /* Social Section */
    .social-section {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    .social-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #ffffff;
      margin-bottom: 0.75rem;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .social-icons {
      display: flex;
      gap: 0.75rem;
      align-items: center;
    }

    .social-circle {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background-color: #292825;
      display: flex;
      justify-content: center;
      align-items: center;
      color: #ffffff;
      text-decoration: none;
      transition:
        background-color 0.2s ease,
        transform 0.2s ease;
    }

    .social-circle:hover,
    .social-circle:focus-visible {
      background-color: #3d3c37;
      transform: translateY(-2px);
      outline: none;
    }

    .social-icon-svg {
      width: 18px;
      height: 18px;
      fill: currentColor;
    }

    /* Divider */
    .divider {
      border: none;
      height: 1px;
      background-color: #2a2925;
      margin: 0 0 1.5rem 0;
    }

    /* Bottom row / Copyright */
    .bottom-row {
      font-size: 0.8rem;
      color: #9e9d98;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    /* Responsive Adjustments */
    @media (max-width: 768px) {
      footer {
        padding: 2.5rem 1.25rem 1.5rem;
      }

      .top-row {
        flex-direction: column;
        gap: 2rem;
      }

      .social-circle {
        width: 44px;
        height: 44px;
      }

      .social-icons {
        flex-wrap: wrap;
      }
    }
  `;

  private _getFallbackIcon(name: string) {
    // Default Instagram SVG icon fallback for social circles
    if (name.toLowerCase().includes('instagram')) {
      return html`
        <svg class="social-icon-svg" viewBox="0 0 24 24">
          <path
            d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
          />
        </svg>
      `;
    }

    // Default simple dot icon for general placeholders matching the design
    return html`
      <svg class="social-icon-svg" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="5" />
      </svg>
    `;
  }

  render() {
    return html`
      <footer>
        <div class="footer-wrapper">
          <div class="top-row">
            <div class="brand-section">
              <h3 class="brand-title">${this.brandTitle}</h3>
              <p class="brand-description">${this.description}</p>
              <a class="contact-email" href="mailto:info@rideforthewild.com"
                >info@rideforthewild.com</a
              >
            </div>

            <div class="social-section">
              ${this.socialLabel
                ? html`<div class="social-label">${this.socialLabel}</div>`
                : ''}
              <div class="social-icons">
                ${this.socialLinks.map(
                  (link) => html`
                    <a
                      class="social-circle"
                      href=${link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label=${link.name}
                      title=${link.name}
                    >
                      ${link.iconSvg
                        ? link.iconSvg
                        : this._getFallbackIcon(link.name)}
                    </a>
                  `
                )}
              </div>
            </div>
          </div>

          <hr class="divider" />

          <div class="bottom-row">${this.copyrightText}</div>
        </div>
      </footer>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rftw-footer': RftwFooter;
  }
}

